import { COMPACT_DIALOG, preventNativeSubmit } from './dialogForm';
import {
  Checkbox,
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icons,
  Input,
  PhoneInput,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons';
import { useForm } from 'react-hook-form';
import { PiUserPlus } from 'react-icons/pi';
import * as z from 'zod';
import { useRegistration } from './authApi';
import { useLogin } from './LoginContext';
import { getErrorDetail } from './responseErrors';

const FormSchema = z
  .object({
    firstName: z.string().nonempty({ message: 'Required' }),
    lastName: z.string().nonempty({ message: 'Required' }),
    email: z.string().email().nonempty({ message: 'Required' }),
    password: z
      .string()
      .regex(new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{12,}$/), {
        message:
          'Must be at least 12 characters and contain one uppercase and one lowercase character and one number.',
      }),
    companyName: z.string().nonempty({ message: 'Required' }),
    phoneNumber: z.string().nonempty({ message: 'Required' }),
    acceptPrivatePolicy: z.boolean().refine((accepted) => accepted, {
      message: 'You must accept the privacy policy',
    }),
  })
  .required();

type RegistrationFormValues = z.infer<typeof FormSchema>;

export function RegistrationCard() {
  const { openDialog, showDialog, closeDialog } = useLogin();
  const { mutateAsync: registerUser, isPending } = useRegistration();

  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      companyName: '',
      phoneNumber: '',
      acceptPrivatePolicy: false,
    },
  });

  const handleSubmit = async ({
    acceptPrivatePolicy: _accepted,
    ...registration
  }: RegistrationFormValues) => {
    try {
      await registerUser(registration);
      form.reset();
      showDialog('registrationSuccess');
    } catch (error) {
      form.setError('root', {
        message:
          (await getErrorDetail(error)) ??
          'Registration failed. Please try again.',
      });
    }
  };

  return (
    <DialogForm
      className={COMPACT_DIALOG.root}
      showDialog={openDialog === 'registration'}
      // Stay open while registering, so the success dialog can't pop up later.
      onOpenChange={(open) => !open && !isPending && closeDialog()}
      size="md"
    >
      <DialogForm.Header>
        <DialogForm.Title icon={<PiUserPlus className="size-5" />}>
          Ready to get started?
        </DialogForm.Title>
        <DialogForm.Description>
          Enter your information below to register a new account
        </DialogForm.Description>
      </DialogForm.Header>
      <Form {...form}>
        <DialogForm.Body observeResize={false}>
          <form
            id="oceanio-registration-form"
            className="space-y-4"
            noValidate
            onSubmit={preventNativeSubmit}
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First name<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors('firstName')}>
                      <Input
                        autoComplete="disabled"
                        type="text"
                        placeholder="First name"
                        className="rounded"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last name<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors('lastName')}>
                      <Input
                        autoComplete="disabled"
                        type="text"
                        placeholder="Last name"
                        className="rounded"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Company name<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl
                      onChange={() => form.clearErrors('companyName')}
                    >
                      <Input
                        type="text"
                        placeholder="Company name"
                        className="rounded"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Phone Number<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl
                      onChange={() => form.clearErrors('phoneNumber')}
                    >
                      <PhoneInput placeholder="Phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    E-mail<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl onChange={() => form.clearErrors('email')}>
                    <Input
                      autoComplete="disabled"
                      type="text"
                      placeholder="Email"
                      className="rounded"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <div className="flex w-full max-w-sm items-center space-x-2">
                      <span>Password</span>
                      <span className="text-red-500">*</span>{' '}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <QuestionMarkCircledIcon className="ml-2 text-[#3A899B] font-bold" />
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#3A899B] border-[#3A899B] rounded-md text-white">
                            Must be at least 12 characters and contain one
                            uppercase and one lowercase character and one
                            number.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </FormLabel>
                  <FormControl onChange={() => form.clearErrors('password')}>
                    <Input
                      autoComplete="disabled"
                      type="password"
                      placeholder="Password"
                      className="rounded"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="acceptPrivatePolicy"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <div className="items-top flex space-x-2">
                    <Checkbox
                      id="accept-terms"
                      onCheckedChange={(value) =>
                        field.onChange(value === true)
                      }
                      checked={field.value}
                      className="data-[state=checked]:bg-[#3A899B] border-[#3A899B]"
                    />
                    <div className="grid gap-1.5 leading-none">
                      <FormLabel
                        htmlFor="accept-terms"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Accept terms and conditions
                        <span className="text-red-500">*</span>
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        You agree to our{' '}
                        <a
                          href="https://gatehousemaritime.com/legal-documents/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          legal documents
                        </a>
                        , including the Terms of Use, Privacy Policy and Cookie
                        Policy.
                      </p>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.root && (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}
          </form>
        </DialogForm.Body>
      </Form>
      <DialogForm.Footer className={COMPACT_DIALOG.footer}>
        <DialogForm.Cancel disabled={isPending}>Cancel</DialogForm.Cancel>
        <DialogForm.Submit
          id="registration-submit"
          form="oceanio-registration-form"
          variant="oceanio"
          disabled={isPending}
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          {isPending && <Icons.spinner className="mr-2 size-4 animate-spin" />}
          CREATE ACCOUNT
        </DialogForm.Submit>
      </DialogForm.Footer>
    </DialogForm>
  );
}
